// In-app, client-side preview for Word documents (.docx/.doc). The original
// file is never modified — we fetch the raw bytes from the access-controlled
// download endpoint and render a *read-only* view.
//
// SECURITY: the rendered HTML (from mammoth) is injected ONLY into a sandboxed
// iframe via `srcdoc`, with an empty `sandbox=""` attribute (no allow-scripts,
// no allow-same-origin). That neutralizes any active content a malicious
// document might smuggle in: scripts can't run, the frame has an opaque origin,
// and it can't reach our DOM, cookies, or storage. We never use
// dangerouslySetInnerHTML / innerHTML on the app DOM for this content.
//
// EXCEL PREVIEW IS DISABLED: it relied on the `xlsx` (SheetJS) npm package,
// which has unpatched High-severity Prototype-Pollution (GHSA-4r6h-8v6p-xvw6)
// and ReDoS (GHSA-5pgg-2g8v-p4x9) advisories with no fixed version on npm. The
// dangerous PARSE of untrusted workbook bytes ran in the app's JS context
// BEFORE the result reached the sandboxed iframe, so the iframe did not contain
// that risk. Until the parser is replaced (isolated worker or server-side
// conversion), spreadsheets are download-only. See docs/code-review-2026-06-28.md
// items 1 & 2.
//
// The heavy lib (mammoth) is dynamically imported INSIDE the effect so it lands
// in its own lazy chunk and doesn't bloat the main bundle.

import { useEffect, useState } from "preact/hooks";
import { Download } from "lucide-preact";
import { Modal } from "./Modal";
import { getTeamsBearer } from "../lib/teamsContext";

interface PreviewAttachment {
  id: number;
  filename: string;
  content_type: string;
}

interface Props {
  attachment: PreviewAttachment;
  onClose: () => void;
}

const WORD_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/msword", // .doc
]);

// Only Word documents are previewable in-app. Excel is intentionally excluded
// (download-only) — see the security note at the top of this file.
export const OFFICE_PREVIEWABLE = new Set<string>([...WORD_TYPES]);

// Wraps the library-produced fragment in a minimal, self-contained document so
// the sandboxed iframe renders it legibly without touching our app styles.
//
// FARB-SONDERFALL: die Hex-Werte im <style> unten sind absichtlich literal,
// nicht `var(--color-…)`. Dieses Dokument geht per `srcdoc` in ein
// `sandbox=""`-iframe OHNE `allow-same-origin` — es bekommt ein eigenes,
// opakes Dokument mit eigenem `:root`. CSS-Custom-Properties des Eltern-
// Dokuments (unsere Theme-Tokens) vererben sich NIE über eine Dokumentgrenze,
// sandboxed oder nicht — das iframe kann `var(--color-on-surface)` etc. also
// gar nicht auflösen. Die Vorschau bildet zudem immer ein Word-Dokument auf
// weißem "Papier" ab (dunkelmodus-unabhängig), daher feste Werte statt Tokens.
function wrapHtml(inner: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>
    :root { color-scheme: light; }
    html, body { margin: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      font-size: 14px;
      line-height: 1.55;
      color: #1a1a1a;
      background: #ffffff;
      padding: 24px;
      word-wrap: break-word;
    }
    h1, h2, h3, h4 { line-height: 1.3; }
    p { margin: 0 0 0.75em; }
    img { max-width: 100%; height: auto; }
    a { color: #1d4ed8; }
    table { border-collapse: collapse; width: auto; max-width: 100%; margin: 0 0 1em; }
    th, td { border: 1px solid #cbd5e1; padding: 4px 8px; text-align: left; vertical-align: top; white-space: pre-wrap; }
    th { background: #f1f5f9; font-weight: 600; }
    tr:nth-child(even) td { background: #f8fafc; }
  </style></head><body>${inner}</body></html>`;
}

type LoadState =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "word"; html: string };

export function AttachmentPreviewModal(props: Props): preact.JSX.Element {
  const { attachment, onClose } = props;
  const [state, setState] = useState<LoadState>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ phase: "loading" });

    void (async () => {
      try {
        const bearer = getTeamsBearer();
        const res = await fetch(`/api/attachments/${attachment.id}/download`, {
          credentials: "include",
          headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
        });
        if (!res.ok) {
          const msg =
            res.status === 401 || res.status === 403
              ? "Keine Berechtigung, diese Datei anzusehen."
              : `Datei konnte nicht geladen werden (Fehler ${res.status}).`;
          if (!cancelled) setState({ phase: "error", message: msg });
          return;
        }
        const arrayBuffer = await res.arrayBuffer();
        if (cancelled) return;

        if (WORD_TYPES.has(attachment.content_type)) {
          const mammoth = await import("mammoth");
          const result = await mammoth.convertToHtml({ arrayBuffer });
          if (cancelled) return;
          setState({ phase: "word", html: wrapHtml(result.value) });
          return;
        }

        setState({
          phase: "error",
          message: "Dieser Dateityp kann nicht in der Vorschau angezeigt werden.",
        });
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[attachment-preview] render failed", err);
        setState({
          phase: "error",
          message:
            "Die Vorschau konnte nicht erstellt werden. Die Datei ist evtl. beschädigt oder zu komplex.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attachment.id, attachment.content_type]);

  const downloadHref = `/api/attachments/${attachment.id}/download`;

  const footer = (
    <div class="flex justify-end">
      <a
        href={downloadHref}
        target="_blank"
        rel="noopener noreferrer"
        download={attachment.filename}
        class="inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-high)] transition-colors"
      >
        <Download size={16} aria-hidden="true" />
        Herunterladen
      </a>
    </div>
  );

  return (
    <Modal title={attachment.filename} onClose={onClose} size="xl" footer={footer}>
      {state.phase === "loading" && (
        <div class="flex items-center justify-center py-16 text-sm text-[color:var(--color-on-surface-variant)]">
          Vorschau wird geladen…
        </div>
      )}

      {state.phase === "error" && (
        <div class="flex flex-col items-center gap-3 py-12 text-center">
          <p class="text-sm text-[color:var(--color-on-surface-variant)]">
            {state.message}
          </p>
          <a
            href={downloadHref}
            target="_blank"
            rel="noopener noreferrer"
            download={attachment.filename}
            class="inline-flex items-center gap-2 rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-sm font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-high)] transition-colors"
          >
            <Download size={16} aria-hidden="true" />
            Stattdessen herunterladen
          </a>
        </div>
      )}

      {state.phase === "word" && (
        <iframe
          // Empty sandbox: no scripts, opaque origin, no access to our DOM,
          // cookies, storage, or top navigation. Read-only render only.
          sandbox=""
          srcdoc={state.html}
          title={`Vorschau: ${attachment.filename}`}
          class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]"
          style={{ height: "min(70vh, 800px)" }}
        />
      )}
    </Modal>
  );
}
